import React, { useState } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { updateProfile } from '../../store/slices/authSlice';
import AvatarUploader from '../../components/common/AvatarUploader';
import Navigation from '../../components/portal/Navigation';

// Styled Components
const PageWrapper = styled.div`
  min-height: 100vh;
  background: #ffffff;
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 3;
  max-width: 1200px;
  margin: 0 auto;
  padding: var(--desktop-page-padding-x, 2rem);
  padding-top: 6rem;

  @media (max-width: 768px) {
    padding: 1rem var(--mobile-page-padding-x);
    padding-top: 1rem;
  }
`;

const ProfileCard = styled(motion.div)`
  margin-bottom: 1.5rem;
  background: #ffffff;
  color: #111827;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.06);
  transition: transform .25s ease, box-shadow .25s ease, border-color .25s ease, opacity .4s ease;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 1rem;
    margin-bottom: 1rem;
    border-radius: 12px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }
`;

const ProfileHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 2rem;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 1.5rem;
    padding: 1.25rem;
    margin-bottom: 1.5rem;
    border-radius: 16px;
  }
`;

const AvatarSection = styled.div`
  flex-shrink: 0;
`;

const HeaderInfo = styled.div`
  flex: 1;

  h1 {
    font-size: 2rem;
    color: #111827;
    margin-bottom: 0.5rem;
  }

  .role-badge {
    display: inline-block;
    padding: 0.5rem 1rem;
    border-radius: 20px;
    background: linear-gradient(135deg, var(--primary-orange) 0%, #ff6b35 100%);
    color: white;
    font-size: 0.875rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  .email {
    color: #4b5563;
    font-size: 1rem;
    margin-top: 0.5rem;
    display: flex;
    align-items: center;
  }

  @media (max-width: 640px) {
    h1 { font-size: 1.5rem; }
    .role-badge { font-size: 0.75rem; padding: 0.35rem 0.75rem; }
    .email { font-size: 0.9rem; }
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  color: #111827;
  margin-bottom: 1.5rem;
  @media (max-width: 768px) {
    font-size: 1.125rem;
    margin-bottom: 1rem;
    text-align: left;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  label {
    font-weight: 700;
    color: #6b7280;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 0.25rem;
  }

  input, textarea {
    padding: 0.875rem;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 12px;
    font-size: 1rem;
    transition: all 0.3s ease;
    background: #f9fafb;
    color: #111827;
    width: 100%;

    @media (max-width: 640px) {
      font-size: 0.95rem;
      padding: 0.75rem;
    }

    &:focus {
      outline: none;
      border-color: var(--primary-orange);
      box-shadow: 0 0 0 3px rgba(255, 87, 34, 0.1);
    }

    &:disabled {
      background: rgba(249, 250, 251, 0.5);
      border-color: transparent;
      padding: 0.5rem 0;
      color: #111827;
      font-weight: 600;
      font-size: 1.1rem;
      cursor: default;
      -webkit-appearance: none;
      -moz-appearance: none;
      appearance: none;
    }
  }

  textarea {
    resize: vertical;
    min-height: 100px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Button = styled(motion.button)<{ variant?: 'primary' | 'secondary' }>`
  padding: 0.875rem 2rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1rem;
  border: none;
  cursor: pointer;
  background: ${props => props.variant === 'secondary'
    ? 'transparent'
    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  color: ${props => props.variant === 'secondary' ? '#667eea' : 'white'};
  border: ${props => props.variant === 'secondary' ? '2px solid #667eea' : 'none'};
  box-shadow: ${props => props.variant === 'secondary'
    ? 'none'
    : '0 4px 15px rgba(102, 126, 234, 0.3)'};
  transition: all 0.3s ease;

  @media (max-width: 640px) {
    font-size: 0.95rem;
    padding: 0.75rem 1.25rem;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.variant === 'secondary'
      ? '0 4px 15px rgba(102, 126, 234, 0.2)'
      : '0 6px 20px rgba(102, 126, 234, 0.4)'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
`;

const StatCard = styled(motion.div)`
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 16px;
  padding: 1.5rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  .stat-value {
    font-size: 1.75rem;
    font-weight: 800;
    color: #111827;
  }

  .stat-label {
    font-size: 0.75rem;
    color: #6b7280;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
`;

const SuccessMessage = styled(motion.div)`
  background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
  color: white;
  padding: 1rem;
  border-radius: 12px;
  margin-bottom: 1rem;
  text-align: center;
  font-weight: 600;
`;

const AdminProfile: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const [isEditing, setIsEditing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    firstName: user?.firstName || user?.username || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: user?.phone || user?.phoneNumber || '',
  });

    if (!user) {
        // Show a loading spinner or redirect to login
        return <div>{t('portal.common.loading', 'Loading...')}</div>;
    }
    if (user.role !== 'admin') {
        // Optionally show a "Not authorized" message
        return <div>{t('portal.common.not_authorized', 'Not authorized')}</div>;
    }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    setSaving(true);
    try {
      await dispatch(updateProfile(formData)).unwrap();
      setShowSuccess(true);
      setIsEditing(false);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error: any) {
      const msg = typeof error === 'string' ? error : (error?.message || 'Failed to update profile');
      setErrorMsg(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      firstName: user.firstName || user.username || '',
      lastName: user.lastName || '',
      email: user.email,
      phone: user.phone || user.phoneNumber || '',
    });
    setIsEditing(false);
  };

  const getInitials = () => {
    const firstInitial = user.firstName?.charAt(0) || user.username?.charAt(0);
    const lastInitial = user.lastName?.charAt(0) || '';
    return `${firstInitial}${lastInitial}`.toUpperCase();
  };

  return (
    <PageWrapper>
      <Navigation />
      <ContentWrapper>
        {showSuccess && (
          <SuccessMessage
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
          >
            {t('portal.profile.saved', 'Profile updated successfully!')} ✓
          </SuccessMessage>
        )}
        {errorMsg && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            style={{ background: 'linear-gradient(135deg,#fc4a1a,#f7b733)', color: 'white', padding: '1rem', borderRadius: 12, marginBottom: '1rem', textAlign: 'center', fontWeight: 600 }}
          >
            ⚠️ {errorMsg}
          </motion.div>
        )}

        <ProfileCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <ProfileHeader>
            <AvatarSection>
              <AvatarUploader />
            </AvatarSection>
            <HeaderInfo>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <div className="role-badge">
                  👑 {t('portal.register.administrator', 'Administrator')}
                </div>
                <h1>{user.firstName || user.username} {user.lastName || ''}</h1>
                <div className="email">
                  <span style={{ marginRight: '8px' }}>✉️</span>
                  {user.email}
                </div>
                {user.phone && (
                  <div className="email" style={{ marginTop: '4px' }}>
                    <span style={{ marginRight: '8px' }}>📱</span>
                    {user.phone}
                  </div>
                )}
              </motion.div>
            </HeaderInfo>
          </ProfileHeader>

          <form onSubmit={handleSubmit}>
            {isEditing && (
              <>
                <SectionTitle>{t('portal.profile.personal_info', 'Personal Information')}</SectionTitle>

                <FormGrid>
                  <FormGroup>
                    <label htmlFor="firstName">{t('portal.profile.first_name', 'First Name')}</label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <label htmlFor="lastName">{t('portal.profile.last_name', 'Last Name')}</label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <label htmlFor="email">{t('portal.profile.email', 'Email Address')}</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <label htmlFor="phone">{t('portal.profile.phone', 'Phone Number')}</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      placeholder="+31 6 12345678"
                    />
                  </FormGroup>
                </FormGrid>
              </>
            )}

            <ButtonGroup>
              {isEditing ? (
                <>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleCancel}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {t('portal.common.cancel', 'Cancel')}
                  </Button>
                  <Button
                    type="submit"
                    disabled={saving}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {saving ? t('portal.profile.saving', 'Saving…') : t('portal.common.save_changes', 'Save Changes')}
                  </Button>
                </>
              ) : (
                <Button
                  type="button"
                  onClick={() => setIsEditing(true)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {t('portal.common.edit_profile', 'Edit Profile')}
                </Button>
              )}
            </ButtonGroup>
          </form>

          <SectionTitle style={{ marginTop: '3rem' }}>{t('portal.profile.account_stats', 'Account Statistics')}</SectionTitle>
          <StatsGrid>
            <StatCard
              whileHover={{ scale: 1.02 }}
              transition={{ type: 'spring', stiffness: 300 }}
            >
              <div className="stat-value">{new Date(user.createdAt).toLocaleDateString()}</div>
              <div className="stat-label">{t('portal.profile.member_since', 'Member Since')}</div>
            </StatCard>

            <StatCard
              whileHover={{ scale: 1.02 }}
              transition={{ type: 'spring', stiffness: 300 }}
            >
              <div className="stat-value">{user.isActive ? t('portal.common.active', 'Active') : t('portal.common.inactive', 'Inactive')}</div>
              <div className="stat-label">{t('portal.profile.account_status', 'Account Status')}</div>
            </StatCard>

            <StatCard
              whileHover={{ scale: 1.02 }}
              transition={{ type: 'spring', stiffness: 300 }}
            >
              <div className="stat-value">{t('portal.profile.access_full', 'Full')}</div>
              <div className="stat-label">{t('portal.profile.access_level', 'Access Level')}</div>
            </StatCard>
          </StatsGrid>
        </ProfileCard>

      </ContentWrapper>
    </PageWrapper>
  );
};

export default AdminProfile;
